import { useMemo } from 'react'
import { Button } from '@mantine/core'
import {
  adviceLine,
  adviseWindow,
  findCar,
  formatClock,
  fuelInCar,
  fuelStopsSince,
  fuelStrategy,
  greenPace,
  isInPits,
  lastCrossingOf,
  planFuelsAt,
  resolveWindows,
  strategyRates,
  strategySetup,
  stoppedInWindow,
  unfilledStop,
  windowStateAt,
} from '@racedash/shared'
import { Tile } from '../Tile'
import { stopKey, useFuelBurn } from '../useFuelBurn'
import type { WidgetProps } from './props'

/**
 * Fuel against the pit windows: how many laps are in the car, whether that
 * reaches the next window and the one after, and so whether this window has
 * to take fuel.
 *
 * For a race with fixed windows. The fuel is the event's measure — from the
 * last full tank, yellow laps at their own rate — and the plan's numbers
 * decide the rest; see fuelStrategy. Before the car has run in the race, the
 * plan's starting load from the start.
 *
 * Between a stop and the fuel station's figure for it, the fills cannot say
 * whether fuel went in. The tile asks, and until somebody answers takes the
 * stop to have done what the plan said: a full tank if it was to take fuel,
 * none if not. The figure, when it comes, answers for itself.
 */
export default function FuelStrategy({ car, data, now, context }: WidgetProps<'fuelStrategy'>) {
  const plan = context.plan
  const at = data.asAt(now)
  const { burn, laps } = useFuelBurn(context.fuel, car)
  const sessionId = data.sessionId
  const sessionStart = data.session?.clockStartAt?.toDate() ?? context.sessionStart ?? null

  const windows = useMemo(
    () => (plan ? resolveWindows(plan.regime, { startedAt: sessionStart, endsAt: context.sessionEnd ?? null }) : []),
    [plan, sessionStart, context.sessionEnd],
  )

  const mine = useMemo(() => (car ? data.laps.filter((l) => l.car === car) : []), [data.laps, car])
  const lastCrossing = lastCrossingOf(mine, car)
  const inPits = isInPits(findCar(data.standings, car))
  const stops = useMemo(() => (car ? data.pitStops.filter((s) => s.car === car) : []), [data.pitStops, car])
  const stopsKey = stops.map((s) => `${s.stopNumber}@${s.completedAt?.toMillis() ?? ''}`).join(',')
  const answers = sessionId ? context.fuel?.stops?.[sessionId] : undefined
  const open = windowStateAt(windows, at).open
  // A window already stopped in has had its stop.
  const stopped = stoppedInWindow(open, at, stops, mine, inPits) != null
  // Nor one that has closed since the car last crossed the line, which the
  // strategy, counting from that crossing, would otherwise still offer.
  const passed = windowStateAt(windows, at).passed
  const ahead = useMemo(
    () => windows.filter((w, i) => i >= passed && !(stopped && open && w.index === open.index)),
    [windows, stopped, open, passed],
  )

  const view = useMemo(() => {
    if (!plan || !burn) return null
    const tank = plan.fuel?.tankLitres
    const rates = strategyRates(plan.fuel, burn.rates)
    const pace = greenPace(laps, sessionId ?? undefined)
    const raceStart = sessionId != null ? burn.sessionStarts[sessionId] : undefined
    const running = lastCrossing != null && raceStart != null && burn.total > raceStart
    const inCar =
      running && tank && rates.green != null && rates.fcy != null
        ? fuelInCar(burn, {
            tank,
            green: rates.green,
            fcy: rates.fcy,
            race: { session: sessionId!, startLitres: plan.fuel?.startLitres },
          })
        : null
    let now = running && inCar ? { at: lastCrossing, litres: inCar.litres, stopsMade: fuelStopsSince(burn, raceStart!) } : undefined

    // A stop the fuel station has not reported on yet: what the team said of
    // it, else what the plan had it do as it stood when the car came in.
    const inLap = inPits ? Math.max(0, ...mine.filter((l) => l.completedAt).map((l) => l.lap)) : 0
    const pending =
      now && car && tank
        ? unfilledStop(burn, raceStart!, stops, inPits && inLap > 0 ? { lap: inLap, at: lastCrossing! } : null)
        : null
    let stop: { out: Date | null; stopNumber: number; answer: boolean | null; fuelled: boolean } | null = null
    if (pending && now && tank) {
      const since = burn.span(raceStart! + pending.lap, burn.total)
      const used = (since.laps - since.fcyLaps) * rates.green! + since.fcyLaps * rates.fcy!
      const answer = answers?.[stopKey(car!, pending.stopNumber)] ?? null
      const before = strategySetup(plan, {
        measured: burn.rates,
        pace,
        windows,
        now: { at: pending.pitIn, litres: Math.min(tank, now.litres + used), stopsMade: now.stopsMade },
      })
      const fuelled = answer ?? (before.input ? planFuelsAt(before.input, pending.pitIn) : true)
      if (fuelled) now = { ...now, litres: tank - used, stopsMade: now.stopsMade + 1 }
      stop = { out: pending.out, stopNumber: pending.stopNumber, answer, fuelled }
    }

    const setup = strategySetup(plan, { measured: burn.rates, pace, windows: ahead, now })
    if (!setup.input) return { missing: setup.missing }
    const strategy = fuelStrategy(setup.input)
    const advice = adviseWindow(strategy, setup.input)
    return { missing: null, setup, strategy, advice, litres: now?.litres ?? null, running, stop }
    // Recomputed when the car crosses the line or the fuel moves, not every tick.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plan, burn, laps, sessionId, lastCrossing?.getTime(), ahead, windows, stopsKey, answers, car, inPits])

  if (!car) return <Blank text="No car" />
  if (!plan) return <Blank text="No race plan" />
  if (!view) return <Blank text="Waiting for the fuel" />
  if (view.missing) return <Blank text={`The plan needs ${view.missing.join(', ')}`} />

  const { setup, strategy, advice, litres, running, stop } = view
  const { line, tone } = adviceLine(advice, (d) => formatClock(d))
  const answerStop = context.fuel?.answerStop
  const outside = advice.call === 'short' && advice.by != null
  const planned = advice.plan.map((n) => `W${n}`).join(', ')
  const reach = (spare: number | null | undefined, label: string) =>
    spare == null ? null : spare >= 0 ? `${label} ✓ +${Math.floor(spare)}` : `${label} ✗ ${Math.ceil(spare)}`

  return (
    <Tile label="Fuel strategy" tone={tone === 'critical' ? 'critical' : tone === 'yellow' ? 'yellow' : undefined}>
      <div className="pitwindow">
        <div className="pitwindow-time">
          {Math.floor(strategy.lapsInCar)} laps of fuel
          {litres != null && <span className="fuelstrat-litres"> {litres.toFixed(1)} L</span>}
        </div>
        <div className="pitwindow-laps">{line}</div>
        {stop && (
          <div className="fuelstrat-stop">
            {stop.out ? `Stopped ${formatClock(stop.out)}` : 'In the pits'} ·{' '}
            {stop.answer == null ? (stop.fuelled ? 'taken as fuelled' : 'taken as no fuel') : stop.fuelled ? 'fuelled' : 'no fuel'}
            {answerStop && sessionId && car && (
              <>
                {stop.answer == null && ' · Fuelled?'}
                {[true, false].map((yes) => (
                  <Button
                    key={String(yes)}
                    size="compact-xs"
                    ml={6}
                    variant={stop.answer === yes ? 'filled' : 'default'}
                    onClick={() => void answerStop(sessionId, car, stop.stopNumber, yes)}
                  >
                    {yes ? 'Yes' : 'No'}
                  </Button>
                ))}
              </>
            )}
          </div>
        )}
        {advice.window && !outside && (
          <div className="fuelstrat-reach">
            {[
              reach(advice.atClose, `W${advice.window.index} close`),
              advice.following && reach(advice.following.atClose, `W${advice.following.window.index} close`),
            ]
              .filter(Boolean)
              .join(' · ')}
          </div>
        )}
        <div className="pitwindow-foot">
          {outside
            ? `Plan: fuel by ${formatClock(advice.by!)}${planned ? `, then in ${planned}` : ''}`
            : advice.plan.length > 0
              ? `Plan: fuel in ${planned}`
              : advice.call === 'short' || advice.call === 'impossible'
                ? 'No way found to the flag'
                : 'No more fuel stops'}
          {advice.splashes > 0 && ` + ${advice.splashes} splash${advice.splashes === 1 ? '' : 'es'}`}
          {setup.input!.stopsOwed > 0 && ` · ${setup.input!.stopsOwed} required`}
          {!running && ' · from the start'}
        </div>
      </div>
    </Tile>
  )
}

function Blank({ text }: { text: string }) {
  return (
    <Tile label="Fuel strategy">
      <div className="pitwindow-blank">{text}</div>
    </Tile>
  )
}
